import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
    isOnline: boolean;
    isReconnecting: boolean;
    lastOnlineTime: Date | null;
    connectionType: string | null;
}

export const useNetworkStatus = () => {
    const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
        isOnline: navigator.onLine,
        isReconnecting: false,
        lastOnlineTime: navigator.onLine ? new Date() : null,
        connectionType: null,
    });

    // 获取连接类型信息
    const getConnectionInfo = useCallback(() => {
        const connection = (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;

        if (connection) {
            return {
                effectiveType: connection.effectiveType,
                downlink: connection.downlink,
                rtt: connection.rtt,
                saveData: connection.saveData,
            };
        }
        return null;
    }, []);

    // 处理在线状态变化
    const handleOnline = useCallback(() => {
        const connectionInfo = getConnectionInfo();
        setNetworkStatus(prev => ({
            ...prev,
            isOnline: true,
            isReconnecting: false,
            lastOnlineTime: new Date(),
            connectionType: connectionInfo?.effectiveType || null,
        }));

        console.log('网络连接已恢复');
    }, [getConnectionInfo]);

    // 处理离线状态变化
    const handleOffline = useCallback(() => {
        setNetworkStatus(prev => ({
            ...prev,
            isOnline: false,
            isReconnecting: false,
            connectionType: null,
        }));

        console.log('网络连接已断开');
    }, []);

    // 手动重连
    const reconnect = useCallback(async () => {
        if (!navigator.onLine) {
            return false;
        }

        setNetworkStatus(prev => ({
            ...prev,
            isReconnecting: true,
        }));

        try {
            // 尝试发送一个简单的网络请求来测试连接
            const response = await fetch('/api/health/', {
                method: 'GET',
                cache: 'no-cache',
                signal: AbortSignal.timeout(5000), // 5秒超时
            });

            if (response.ok) {
                handleOnline();
                return true;
            } else {
                throw new Error('Network test failed');
            }
        } catch (error) {
            console.error('重连失败:', error);
            setNetworkStatus(prev => ({
                ...prev,
                isReconnecting: false,
            }));
            return false;
        }
    }, [handleOnline]);

    // 监听网络状态变化
    useEffect(() => {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // 监听连接变化（如果支持）
        const connection = (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;

        const handleConnectionChange = () => {
            const connectionInfo = getConnectionInfo();
            setNetworkStatus(prev => ({
                ...prev,
                connectionType: connectionInfo?.effectiveType || null,
            }));
        };

        if (connection) {
            connection.addEventListener('change', handleConnectionChange);
        }

        // 初始化连接信息
        const initialConnectionInfo = getConnectionInfo();
        if (initialConnectionInfo) {
            setNetworkStatus(prev => ({
                ...prev,
                connectionType: initialConnectionInfo.effectiveType,
            }));
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);

            if (connection) {
                connection.removeEventListener('change', handleConnectionChange);
            }
        };
    }, [handleOnline, handleOffline, getConnectionInfo]);

    return {
        ...networkStatus,
        reconnect,
        connectionInfo: getConnectionInfo(),
    };
};